import {
    alpha,
    Box,
    Button,
    MuiAlert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@leap4/frontend';
import { useMemo, useState } from 'react';

type ReportMode = 'pixel' | 'advertiser';
interface ExplanationItem {
    key: string;
    label: string;
    text: string;
    color: string;
    targets: string[];
}

const cellTarget = (rowIndex: number, columnIndex: number): string => `${rowIndex}:${columnIndex}`;

const ReportingGuide = () => {
    const [mode, setMode] = useState<ReportMode>('pixel');
    const [activeKey, setActiveKey] = useState<string | null>(null);

    const modeTitle = mode === 'pixel' ? 'Impression Date Report' : 'Conversion Date Report';
    const modeIntro = mode === 'pixel'
        ? 'Use this when you want to analyze served and matched performance by impression date.'
        : 'Use this when you want to analyze raw advertiser event activity by conversion date.';

    const modeRules = mode === 'pixel'
        ? [
            'Date axis is Impression Date — it is what the date range filters.',
            'Conversion Date can be added as a dimension alongside Matched Impressions or attributed events, and then shows when each conversion happened — which can be outside the selected range. It is unavailable with Served Impressions, which come from raw pixels and have no conversion side.',
            'The primary metric is Served Impressions.',
            'Reach counts unique IP + device combinations. The device id is not reliably a device — upstream it is sometimes a session id — so treat reach as combinations reached rather than people, and never sum it across rows: read the OVERALL row.',
            'Frequency is impressions divided by reach. Both come from one pass over the raw pixels, so selecting both costs no more than selecting one.',
            'Reach and Frequency are capped at 31 days. Both grow with the window rather than settling, so a longer range would mean a slower scan and a number that keeps climbing. Longer ranges are refused rather than truncated.',
            'Selecting either makes the report noticeably slower: they cannot use the pre-aggregated tables the other impression metrics read.',
            'Frequency is scoped to each row\'s grain and to the date range, and the effect is large: one campaign measured about 2.2 over a month at campaign level and close to 1 in the same month broken out by day and app bundle. Households rarely see one app twice in a day, so a monthly figure accumulates across days and across apps rather than within them. A broken-out report showing a column of ~1s is correct, not broken. Judge against the 4-8 benchmark only at campaign level over a full month.',
            'Revenue, Cost, RPM and CPM are global base metrics — always available, derived from the per-impression rates (no per-campaign setup).',
            'RPM and CPM are ratios (Revenue/Cost ÷ Served × 1000): they average correctly across groupings and are never summed.',
            'Each configured event adds a "<Event> (Attributed)" column (fractional attributed amount); count events also add a "Matched Impression (<Event>)" column (raw distinct matched impressions).',
            'Late Conversions (Malwarebytes only) counts installs that arrived AFTER the attribution window closed, where a visit inside the impression\'s window re-opened it. Credit is fractional, so one install totals 1 however many impressions it reaches. Late Matched Impressions is the distinct impression count behind it.',
            'Late Conversions are deliberately separate from Matched Impressions and never counted in it — an install is either normally attributed or late, never both. Selected on their own, the report shows only impression dates that earned late credit; add Served Impressions and you get every served row, most of them zero.',
            'Late Conversions take 28 days to mature — twice normal attribution. An impression can be claimed by a late install up to 28 days after it served, so the most recent four weeks of impression dates are still filling in and will read low. A zero on last week is "not yet", not "none".',
            'Late volume is a few a day, so read Late Conversions from the range total rather than per-day rows — a day showing 8 and a day showing 2 are usually the same underlying rate.',
            'A spike in Late Matched Impressions is often one shared IP, not one good day: a single install behind a corporate or carrier NAT can credit hundreds of impressions. Late Conversions is unaffected — the credit still splits to total 1 — so trust that column when the two disagree.',
            'Best for attribution and impression-economics (revenue/cost/RPM/CPM) analysis.',
            'A bold OVERALL row is pinned to the bottom of the table: whole-report totals across ALL pages, with RPM/CPM and calculated metrics recomputed over the whole report (never summed). It is captured when the report runs, stays fixed while you paginate, and is the last line of the CSV export.',
        ]
        : [
            'Date axis is Conversion Date.',
            'The primary metric is Advertiser Events (raw event count across all events).',
            'Count events (e.g. Purchase, Visit) show as raw event counts; amount events (e.g. Verified Install Revenue) show as "(Total $)" summed dollars.',
            'Late Conversions (Raw) (Malwarebytes only) counts whole late installs on this axis — no fractional split, since the question here is how many arrived late rather than how the credit divided. It is exclusive: select it or Advertiser Events, not both.',
            'Unlike the impression axis, Late Conversions (Raw) is complete as soon as attribution has run — but it can read HIGH for the last day or two, because an install that has not been attributed yet still looks unattributed and therefore late. Give recent dates a day to settle.',
            'Best for advertiser-side event activity analysis.',
            'A bold OVERALL row is pinned to the bottom of the table: whole-report totals across ALL pages, with calculated metrics recomputed over the whole report (never summed). It is captured when the report runs, stays fixed while you paginate, and is the last line of the CSV export.',
        ];

    const preview = useMemo(() => {
        if (mode === 'pixel') {
            return {
                columns: ['Impression Date', 'Campaign', 'Served Impressions', 'Revenue', 'Cost', 'RPM', 'CPM', 'Visit (Attributed)', 'Matched Impression (Visit)'],
                rows: [
                    ['2026-06-01', 'mb', '224,360', '1,914.20', '2,131.42', '8.53', '9.50', '4,080.10', '3,972'],
                    ['2026-06-02', 'mb', '231,044', '1,963.87', '2,194.92', '8.50', '9.50', '3,880.33', '3,761'],
                    ['OVERALL', '', '455,404', '3,878.07', '4,326.34', '8.52', '9.50', '7,960.43', '7,733'],
                ],
            };
        }
        return {
            columns: ['Conversion Date', 'Campaign', 'Advertiser Events', 'Visit', 'Verified Install Revenue (Total $)'],
            rows: [
                ['2026-06-01', 'mb', '4,305', '4,210', '1,914.20'],
                ['2026-06-02', 'mb', '4,522', '4,420', '1,963.87'],
                ['OVERALL', '', '8,827', '8,630', '3,878.07'],
            ],
        };
    }, [mode]);

    const explanations = useMemo<ExplanationItem[]>(() => {
        if (mode === 'pixel') {
            return [
                {
                    key: 'served',
                    label: 'Served Impressions',
                    text: 'Total impressions served on that impression date row.',
                    color: '#1976d2',
                    targets: [cellTarget(0, 2), cellTarget(1, 2)],
                },
                {
                    key: 'revenue',
                    label: 'Revenue',
                    text: 'Total revenue on that impression date — summed from the per-impression RPM rate (adte_pixel.c5). Global base metric, available on every campaign.',
                    color: '#0288d1',
                    targets: [cellTarget(0, 3), cellTarget(1, 3)],
                },
                {
                    key: 'cost',
                    label: 'Cost',
                    text: 'Total media cost on that impression date — summed from the per-impression CPM rate (adte_pixel.c4). Global base metric, available on every campaign.',
                    color: '#c62828',
                    targets: [cellTarget(0, 4), cellTarget(1, 4)],
                },
                {
                    key: 'rpm',
                    label: 'RPM',
                    text: 'Revenue per mille = Revenue / Served Impressions × 1000. A calculated base metric (ratio), so it is averaged correctly across groupings — never summed.',
                    color: '#00897b',
                    targets: [cellTarget(0, 5), cellTarget(1, 5)],
                },
                {
                    key: 'cpm',
                    label: 'CPM',
                    text: 'Cost per mille = Cost / Served Impressions × 1000. A calculated base metric (ratio), so it is averaged correctly across groupings — never summed.',
                    color: '#5e35b1',
                    targets: [cellTarget(0, 6), cellTarget(1, 6)],
                },
                {
                    key: 'visit_attributed',
                    label: 'Visit (Attributed)',
                    text: 'Attributed amount for the Visit event on that impression date row — fractional attribution credit. Each configured event has its own "(Attributed)" column.',
                    color: '#9c27b0',
                    targets: [cellTarget(0, 7), cellTarget(1, 7)],
                },
                {
                    key: 'visit_matched',
                    label: 'Matched Impression (Visit)',
                    text: 'Raw count of distinct impressions that matched the Visit event — a plain count, not a weighted amount. Count events get one of these per event.',
                    color: '#2e7d32',
                    targets: [cellTarget(0, 8), cellTarget(1, 8)],
                },
                {
                    key: 'total_row',
                    label: 'OVERALL',
                    text: 'Whole-report totals across ALL pages, not just the visible one. Counts and amounts are summed; RPM/CPM and calculated metrics are recomputed over the whole report (never summed). Captured when the report runs, fixed while paginating, and written as the last line of the CSV export.',
                    color: '#455a64',
                    targets: [cellTarget(2, 0), cellTarget(2, 2), cellTarget(2, 3), cellTarget(2, 4), cellTarget(2, 5), cellTarget(2, 6), cellTarget(2, 7), cellTarget(2, 8)],
                },
            ];
        }

        return [
            {
                key: 'advertiser-events',
                label: 'Advertiser Events',
                text: 'Raw advertiser event count on that conversion date row (all events combined).',
                color: '#1976d2',
                targets: [cellTarget(0, 2), cellTarget(1, 2)],
            },
            {
                key: 'visit_raw',
                label: 'Visit',
                text: 'Raw count of Visit events on that conversion date row. Count events appear as their own raw-count column.',
                color: '#9c27b0',
                targets: [cellTarget(0, 3), cellTarget(1, 3)],
            },
            {
                key: 'vir_total',
                label: 'Verified Install Revenue (Total $)',
                text: 'Summed dollar amount for the Verified Install Revenue event on that conversion date row. Amount events (…sum / …commission) appear as "(Total $)" columns.',
                color: '#00897b',
                targets: [cellTarget(0, 4), cellTarget(1, 4)],
            },
            {
                key: 'total_row',
                label: 'OVERALL',
                text: 'Whole-report totals across ALL pages, not just the visible one. Counts and amounts are summed; calculated metrics are recomputed over the whole report (never summed). Captured when the report runs, fixed while paginating, and written as the last line of the CSV export.',
                color: '#455a64',
                targets: [cellTarget(2, 0), cellTarget(2, 2), cellTarget(2, 3), cellTarget(2, 4)],
            },
        ];
    }, [mode]);

    const targetToKey = useMemo(() => {
        const map: Record<string, string> = {};
        explanations.forEach((item) => {
            item.targets.forEach((target) => {
                map[target] = item.key;
            });
        });
        return map;
    }, [explanations]);

    const activeTargets = useMemo(() => {
        if (!activeKey) return new Set<string>();
        const item = explanations.find((explanation) => explanation.key === activeKey);
        return new Set(item?.targets || []);
    }, [activeKey, explanations]);

    return (
        <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Reporting Guide
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Choose a report mode to see what it means in the UI and table output.
                </Typography>
            </Box>

            <MuiAlert severity="info" sx={{ mb: 2 }}>
                Pick one mode per report — it sets the date axis and which metrics are available.
            </MuiAlert>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Core Rules
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Button
                        variant={mode === 'pixel' ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => setMode('pixel')}
                        sx={{ textTransform: 'none' }}
                    >
                        Impression Date Report
                    </Button>
                    <Button
                        variant={mode === 'advertiser' ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => setMode('advertiser')}
                        sx={{ textTransform: 'none' }}
                    >
                        Conversion Date Report
                    </Button>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {modeTitle}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {modeIntro}
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {modeRules.map((rule) => (
                        <Typography component="li" variant="body2" key={rule}>
                            {rule}
                        </Typography>
                    ))}
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Campaign Settings Notes
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    <Typography component="li" variant="body2">
                        Custom Labels rename what users see in the builder and table.
                    </Typography>
                    <Typography component="li" variant="body2">
                        Event Display Labels rename event columns in reports.
                    </Typography>
                    <Typography component="li" variant="body2">
                        Calculated metrics are formulas over <strong>metrics only</strong> — base metrics (Served, Matched, Revenue, Cost, RPM, CPM, Advertiser Events), events, or other calculated metrics (chaining, e.g. <code>roi = revenue / cost</code>). Dimensions (C1–C5, etc.) can no longer be used in formulas.
                    </Typography>
                    <Typography component="li" variant="body2">
                        Calculated metrics are computed in the database, so they sort like any other column and total correctly in the OVERALL row. A formula's operands must all come from the same report source (e.g. Advertiser Events cannot mix with Served/Matched) — a formula that doesn't fit the report you run is skipped rather than shown.
                    </Typography>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Example Table Preview
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                    Preview of how results typically look in {modeTitle}.
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {preview.columns.map((column) => (
                                    <TableCell key={column} sx={{ fontWeight: 600 }}>
                                        {column}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {preview.rows.map((row, rowIndex) => (
                                <TableRow key={`row_${rowIndex}`}>
                                    {row.map((value, valueIndex) => {
                                        const target = cellTarget(rowIndex, valueIndex);
                                        const mappedKey = targetToKey[target];
                                        const activeItem = explanations.find((item) => item.key === activeKey);
                                        const isActive = activeTargets.has(target);
                                        return (
                                        <TableCell
                                            key={`cell_${rowIndex}_${valueIndex}`}
                                            onMouseEnter={() => mappedKey && setActiveKey(mappedKey)}
                                            onMouseLeave={() => mappedKey && setActiveKey(null)}
                                            sx={{
                                                cursor: mappedKey ? 'pointer' : 'default',
                                                fontWeight: row[0] === 'OVERALL' ? 600 : undefined,
                                                transition: 'background-color 0.12s ease',
                                                backgroundColor: isActive && activeItem ? alpha(activeItem.color, 0.20) : 'transparent',
                                                boxShadow: isActive && activeItem ? `inset 3px 0 0 ${activeItem.color}` : 'none',
                                            }}
                                        >
                                            {value}
                                        </TableCell>
                                    )})}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    What highlighted cells mean
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                    Hover a value in the table or a line below to highlight both.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {explanations.map((item) => {
                        const isActive = activeKey === item.key;
                        return (
                            <Box
                                key={item.key}
                                onMouseEnter={() => setActiveKey(item.key)}
                                onMouseLeave={() => setActiveKey(null)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    p: 0.75,
                                    borderRadius: 1,
                                    borderLeft: `4px solid ${item.color}`,
                                    transition: 'background-color 0.12s ease',
                                    backgroundColor: isActive ? alpha(item.color, 0.14) : 'transparent',
                                    cursor: 'default',
                                }}
                            >
                                <Box
                                    sx={{
                                        mt: '4px',
                                        width: 10,
                                        height: 10,
                                        borderRadius: '3px',
                                        backgroundColor: item.color,
                                        flex: '0 0 auto',
                                    }}
                                />
                                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                                    <strong>{item.label}:</strong> {item.text}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Paper>
        </Box>
    );
};

export default ReportingGuide;
